import Service from './Service';

/**
 * Host app (e.g. Ionic + Capacitor) can register native **photo library** picking (`pickImages`)
 * so shared UI avoids WKWebView’s document picker. Capture / record still uses in-webview `getUserMedia`
 * (`MediaUploadTile` fullscreen camera). Web/CRA leaves this unregistered.
 *
 * The bridge should **not throw** for expected failures (permissions, etc.). Call `reportGalleryPickFailure`
 * and return `null` so the platform UI can read the reason via `takeLastGalleryPickFailure()` after
 * `pickGalleryImageFile()` resolves.
 */
class NativeCamera extends Service {
  /** @type {null | (() => Promise<File | null>)} */
  _pickGalleryImageFile = null;

  /** @type {'permission_denied' | 'unknown' | undefined} */
  _lastGalleryPickFailure;

  registerPickGalleryImageFile(impl) {
    this._pickGalleryImageFile = typeof impl === 'function' ? impl : null;
  }

  /** True when the host app registered Capacitor/native picking (do not fall back to `<input type="file">`). */
  hasPickGalleryImageFileImpl() {
    return this._pickGalleryImageFile != null;
  }

  /** Skip programmatic `<input type="file">` (WKWebView sheet) when gallery is bridged natively. */
  avoidWebKitFileInputFallback() {
    return this.hasPickGalleryImageFileImpl();
  }

  /**
   * Report a gallery pick failure without throwing (invoked from the native bridge).
   * @param {'permission_denied' | 'unknown'} kind
   */
  reportGalleryPickFailure(kind) {
    if (kind === 'permission_denied') {
      this._lastGalleryPickFailure = 'permission_denied';
    } else if (kind === 'unknown') {
      this._lastGalleryPickFailure = 'unknown';
    }
  }

  async pickGalleryImageFile() {
    this._lastGalleryPickFailure = undefined;
    const impl = this._pickGalleryImageFile;
    if (!impl) return null;
    try {
      const file = await impl();
      return file instanceof File ? file : null;
    } catch {
      this._lastGalleryPickFailure = 'unknown';
      return null;
    }
  }

  /** Consume reason from the last `pickGalleryImageFile` (bridge reported or unexpected throw). Clears stored value. */
  takeLastGalleryPickFailure() {
    const x = this._lastGalleryPickFailure;
    this._lastGalleryPickFailure = undefined;
    return x;
  }
}

const singleton = new NativeCamera();
export default singleton;
